import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { fortLayout, hexToWorld, slopeLayout, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { companyShield, COMPANY_RED } from '../models/sendas.ts';
import { flagMat } from './chasm.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The fort at the foot of the pass (docs/sendas/guerrero.md, chapter 3, «La noche en el fuerte»), drawn on the Spine's
 * snow (engine/spine.ts repaints the board and lights it, always cold). A small fort of grey stone buried to its
 * middle in drifts, its gabled roof bowed under the snow and set on an axis; a yard each side under each slope, walled
 * low; the stable north of it with the walk-through where the pieces lie; the passage past its south end. The only warm
 * colour is fire: the hearth in its yard, torches on the walls, the farms' lights far across the valley; and the red
 * of the Company's crosses.
 *
 * - `fort`, `fortPast` (acts 2 and 3, and the scenes outside at night or at dawn): the yards; the roof and what it
 *   carries (the snow on each slope by the sim's count, the pieces on the eaves' hooks: shields with their crosses,
 *   sacks of wood); the flag on the ridge flying to the lee side, shaking before the wind turns; the hearth lit or
 *   cold and smoking; the roof tipping (it swings on its axis, the snow pours off the eave in a curtain, a cloud of it
 *   rolls over the yard and a mound is left that sinks away). `fortPast` is the same sixty years back, cleaner.
 * - `fortFar` (the opening): the same fort seen from the slope below, at dusk, the pass's ice above it, and across
 *   the valley the high farms' lights.
 * - `fortClimb` (act 1, the opening scene): the trail up between ice and fallen rock, in switchbacks to the fort's door at
 *   the top; Kel's torch planted at the landing; the rocks' shadows growing on the snow before they land.
 * - `fortTomb`, `fortTable`, `fortHearth` (scenes inside): the hall, a long room under bare rafters: the great hearth
 *   in its north wall, the long table with its eleven places; in the last room, Sanne and Tomé sitting against the
 *   wall, frost on them, the shields across their knees; at the table sixty years back, five places empty.
 *
 * Nothing per particle: the fire, the smoke, the falling snow off the roof and its cloud are shaders on a few meshes.
 * Draw calls: the dressing (one merged mesh), the roof (two slopes, two snow slabs, the pieces on its hooks), the
 * flames (one merged mesh), the hearth's flame and smoke, the flag, the tip's curtain, cloud and mound, a glow layer.
 * No lights of its own (docs/RENDIMIENTO.md: never add point lights): fire light is glow and warm-painted stone.
 */

export const FORT_PLACES = new Set(['fortClimb', 'fortFar', 'fort', 'fortPast', 'fortTomb', 'fortTable', 'fortHearth']);
/** The places inside the hall. */
export const HALL_PLACES = new Set(['fortTomb', 'fortTable', 'fortHearth']);

const S = WORLD_PER_HEX;
const ROW = 0.866 * S;
const STONE = ['#6c717a', '#5d626b', '#777c85', '#535860'];
const DARK_STONE = ['#3e424a', '#353940', '#474b53'];
const WOOD = ['#3a3430', '#4a4038', '#2e2925', '#56483c'];
const ROOF_WOOD = '#3b332d';
const SNOW = new THREE.Color('#e8edf4');
const SNOW_SHADE = new THREE.Color('#b9c4d3');
const ICE = '#a9c6dc';
const ROCK = ['#4a5361', '#3e4653', '#56606e', '#353c48'];
const FIRE = new THREE.Color('#ff8a3a');
const FIRE_DEEP = new THREE.Color('#ff5a1c');
const FARM = new THREE.Color('#ffb36a');

/** The fort's measures (world units): its walls, its ridge, its eaves. */
const FW = 1.45;
const FL = 2.55 * ROW;
const WALL = 1.1;
const RIDGE = 2.3;
const EAVE_X = 2.15;
const EAVE_Y = 1.0;

/** A flame: crossed quads flickering in the vertex shader (one merged mesh for every torch). */
const flameMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 } },
    vertexShader: /* glsl */ `
      attribute float aSeed;
      uniform float uTime;
      varying vec2 vUv;
      varying float vSeed;
      void main() {
        vUv = uv;
        vSeed = aSeed;
        vec3 p = position;
        float f = 0.85 + 0.15 * sin(uTime * 13.0 + aSeed * 7.0) + 0.08 * sin(uTime * 29.0 + aSeed * 3.0);
        p.y *= f;
        p.x += sin(uTime * 6.0 + aSeed * 5.0 + uv.y * 3.0) * 0.03 * uv.y;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      varying vec2 vUv;
      varying float vSeed;
      void main() {
        vec2 c = vUv - vec2(0.5, 0.0);
        float w = 0.5 * (1.0 - vUv.y) * (1.0 - vUv.y) + 0.04;
        float body = smoothstep(w, w * 0.25, abs(c.x)) * smoothstep(1.0, 0.55, vUv.y);
        float core = smoothstep(w * 0.55, 0.0, abs(c.x)) * smoothstep(0.7, 0.1, vUv.y);
        vec3 col = mix(vec3(1.0, 0.32, 0.08), vec3(1.0, 0.82, 0.45), core);
        gl_FragColor = vec4(col * (body + core * 0.6), body);
      }`,
  });

/** Smoke rising from a cold hearth: a tall soft column, its noise drifting up and leaning with the wind. */
const smokeMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uOn: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      uniform float uTime;
      void main() {
        vUv = uv;
        vec3 p = position;
        p.x += uv.y * uv.y * 0.9;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uOn;
      varying vec2 vUv;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float k = n(vec2(vUv.x * 3.0, vUv.y * 4.0 - uTime * 0.7)) * 0.6 + n(vec2(vUv.x * 7.0, vUv.y * 9.0 - uTime * 1.3)) * 0.4;
        float edge = smoothstep(0.5, 0.15, abs(vUv.x - 0.5)) * smoothstep(0.0, 0.15, vUv.y) * smoothstep(1.0, 0.4, vUv.y);
        gl_FragColor = vec4(vec3(0.42, 0.45, 0.5), k * edge * 0.55 * uOn);
      }`,
  });

/** Snow pouring off an eave: a curtain of falling streaks, then gone (uK from 1 down to 0). */
const curtainMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uK: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uK;
      varying vec2 vUv;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float fall = n(vec2(vUv.x * 26.0, vUv.y * 3.0 + uTime * 9.0)) * 0.7 + n(vec2(vUv.x * 60.0, vUv.y * 6.0 + uTime * 14.0)) * 0.5;
        float a = smoothstep(0.35, 0.8, fall) * uK * smoothstep(0.0, 0.1, vUv.x) * smoothstep(1.0, 0.9, vUv.x);
        gl_FragColor = vec4(mix(vec3(0.82, 0.87, 0.95), vec3(1.0), vUv.y), a);
      }`,
  });

/** The cloud a tip throws over the yard: a soft disc of snow dust, billowing out and thinning (uK 1 → 0). */
const cloudMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uTime: { value: 0 }, uK: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uK;
      varying vec2 vUv;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec2 c = vUv - 0.5;
        float r = length(c) * 2.0;
        float b = n(c * 6.0 + uTime * 0.6) * 0.6 + n(c * 13.0 - uTime) * 0.4;
        float a = smoothstep(1.0, 0.35, r + (b - 0.5) * 0.5) * uK * (0.55 + 0.45 * b);
        gl_FragColor = vec4(vec3(0.93, 0.95, 1.0), a);
      }`,
  });

interface Tip {
  at: number;
  sx: number;
}

export class FortPlace {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private timed: THREE.ShaderMaterial[] = [];
  private glow = new GlowLayer(96);
  private glows: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number }[] = [];
  private now = 0;
  /** Where the HUD hangs its marks (world points): each eave's load, its hooks, the pile, the hearth, the flag, the torch. */
  readonly anchors: {
    eaves: (THREE.Vector3 | null)[];
    hooks: THREE.Vector3[][];
    pile: THREE.Vector3 | null;
    hearth: THREE.Vector3 | null;
    flag: THREE.Vector3 | null;
    torch: THREE.Vector3 | null;
    door: THREE.Vector3 | null;
  } = { eaves: [null, null], hooks: [[], []], pile: null, hearth: null, flag: null, torch: null, door: null };
  /** The hearth's side (−1 west, +1 east): eave 0 is on it. */
  readonly side: number = -1;
  // The roof.
  private roof: THREE.Group | null = null;
  private slabs: THREE.Mesh[] = [];
  private lips: THREE.Mesh[] = [];
  private slots: THREE.Group[][] = [[], []];
  private pieceKey = '';
  private shieldProto: THREE.Group | null = null;
  private tip: Tip | null = null;
  private lastTips = -1;
  private curtain: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  private cloud: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  private mound: THREE.Mesh | null = null;
  private flag: { g: THREE.Group; mat: THREE.ShaderMaterial; yaw: number; want: number; warnAt: number } | null = null;
  // The hearth.
  private hearth: { flame: THREE.Mesh; smoke: THREE.Mesh; smokeMat: THREE.ShaderMaterial; at: THREE.Vector3; lit: boolean } | null = null;
  private pile: THREE.Group[] = [];
  // The climb.
  private torch: { g: THREE.Group; at: THREE.Vector3 } | null = null;
  private shadows: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; rock: THREE.Mesh }[] = [];
  private shadowState: { x: number; y: number; t: number }[] = [];
  private tickNow = 0;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
    kit: Kit,
  ) {
    this.group.name = `fort-${place}`;
    const flames = new FlameKit();
    if (place === 'fortClimb') this.buildSlope(kit, flames, s);
    else if (HALL_PLACES.has(place)) this.buildHall(kit, flames);
    else {
      const L = fortLayout(s.seed);
      (this as { side: number }).side = L.side;
      this.buildFort(kit, flames, s, place === 'fortPast');
      if (place === 'fortFar' || place === 'fort' || place === 'fortPast') this.buildFarms();
    }
    const fm = flames.build(this);
    if (fm) this.group.add(fm);
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  std(color?: THREE.ColorRepresentation, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.04, flatShading: true, ...(color !== undefined ? { color } : { vertexColors: true }), ...o });
    this.mats.push(m);
    return m;
  }

  track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  /** A warm glow (fire's light on the snow and the stone), its flicker by `seed`. */
  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number): void {
    this.glows.push({ p: p.clone(), c: color.clone(), size, a, seed: this.glows.length * 1.37 });
  }

  /** Stone lit by fires near it: a colour pulled warm by how close it stands to any of `fires` (the fake light). */
  private warm(c: THREE.ColorRepresentation, x: number, z: number, fires: THREE.Vector3[], k = 1): THREE.Color {
    const out = new THREE.Color(c);
    let w = 0;
    for (const f of fires) {
      const d = Math.hypot(f.x - x, f.z - z);
      w = Math.max(w, Math.max(0, 1 - d / 3.2));
    }
    if (w > 0) out.lerp(new THREE.Color('#d0773a'), w * w * 0.55 * k).multiplyScalar(1 + w * 0.35 * k);
    return out;
  }

  /* ---------- the fort ---------- */

  /** The fort, the yards' walls, the stable and its walk-through, the hearth, the roof on its axis and the flag. */
  private buildFort(kit: Kit, flames: FlameKit, s: BattleState, past: boolean): void {
    const L = fortLayout(s.seed);
    const h = L.side;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dome = new THREE.SphereGeometry(1, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 9);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const g = this.ctx.ground(0, 0);
    const hearthAt = this.hex(L.hearth.q, L.hearth.r);
    const torchAt: THREE.Vector3[] = [];
    // The torches first (their warmth paints the stone round them).
    const doorAt = this.hex(L.door.q, L.door.r);
    const sconce = (x: number, z: number, y: number): void => {
      const p = new THREE.Vector3(x, g + y, z);
      torchAt.push(p);
      kit.add(box, WOOD[2] as string, place(x, g + y - 0.2, z, 0, new THREE.Vector3(0.06, 0.4, 0.06)));
      kit.add(box, '#2b2622', place(x, g + y - 0.02, z, 0, new THREE.Vector3(0.1, 0.08, 0.1)));
      flames.add(p.clone().setY(p.y + 0.02), 0.32, torchAt.length);
      this.light(p.clone().setY(p.y + 0.15), FIRE, 1.6, 0.55);
      this.light(new THREE.Vector3(x, g + 0.05, z), FIRE_DEEP, 3.4, 0.22);
    };
    // By the door on the hearth's side, the far yard's corners, the gateposts at its mouth, the stable's walk-through.
    sconce(h * (FW + 0.12), doorAt.z - 0.55, 1.35);
    sconce(-h * (FW + 0.12), -FL + 0.4, 1.35);
    sconce(-h * (FW + 0.12), FL - 0.4, 1.35);
    sconce(-h * 2.9, -4.75 * ROW, 1.2);
    sconce(-h * 5.6, -4.75 * ROW, 1.2);
    sconce(h * 5.75, 4.2 * ROW, 1.0);
    const fires = [...torchAt, hearthAt];
    const wc = (c: string, x: number, z: number, k = 1): THREE.Color => this.warm(c, x, z, fires, k);
    // The fort: thick walls of grey stone, buried to their middle in drifts, the stones' courses showing.
    for (let i = 0; i < 2; i++) {
      const sx = i === 0 ? -1 : 1;
      for (let k = 0; k < 9; k++) {
        const z = -FL + ((k + 0.5) / 9) * 2 * FL;
        kit.add(box, wc(STONE[(k + i) % 4] as string, sx * FW, z), place(sx * FW, g + WALL / 2, z, 0, new THREE.Vector3(0.36, WALL + (k % 3) * 0.04, (2 * FL) / 9 + 0.02)));
      }
    }
    for (const sz of [-1, 1]) {
      for (let k = 0; k < 4; k++) {
        const x = -FW + ((k + 0.5) / 4) * 2 * FW;
        kit.add(box, wc(STONE[(k + 2) % 4] as string, x, sz * FL), place(x, g + WALL / 2, sz * FL, 0, new THREE.Vector3((2 * FW) / 4 + 0.02, WALL + (k % 2) * 0.05, 0.36)));
      }
      // The gables up to the ridge: stacked courses narrowing.
      for (let k = 0; k < 6; k++) {
        const y = WALL + (k + 0.5) * ((RIDGE - WALL) / 6);
        const w = 2 * FW * (1 - (k + 0.5) / 6.3);
        kit.add(box, wc(STONE[k % 4] as string, 0, sz * FL), place(0, g + y, sz * FL, 0, new THREE.Vector3(w, (RIDGE - WALL) / 6 + 0.01, 0.32)));
      }
    }
    // Courses: a darker line every so often round the walls.
    for (const y of [0.42, 0.82]) {
      for (const sx of [-1, 1]) kit.add(box, DARK_STONE[0] as string, place(sx * (FW + 0.17), g + y, 0, 0, new THREE.Vector3(0.03, 0.04, 2 * FL)));
    }
    // The door on the hearth's side (dark planks, iron bands) and the arrow slits on the far side.
    kit.add(box, '#2a241f', place(h * (FW + 0.18), g + 0.62, doorAt.z, 0, new THREE.Vector3(0.06, 1.15, 0.72)));
    for (const y of [0.3, 0.8]) kit.add(box, '#26282c', place(h * (FW + 0.21), g + y, doorAt.z, 0, new THREE.Vector3(0.02, 0.05, 0.74)));
    kit.add(box, WOOD[3] as string, place(h * (FW + 0.18), g + 1.24, doorAt.z, 0, new THREE.Vector3(0.12, 0.12, 0.94)));
    for (let k = 0; k < 4; k++) kit.add(box, '#0d0e11', place(-h * (FW + 0.19), g + 0.78, -FL + 0.9 + k * 1.15, 0, new THREE.Vector3(0.02, 0.38, 0.08)));
    // Drifts against the fort, deep on the windward north end and its sides: the fort sunk to its middle.
    for (let k = 0; k < 14; k++) {
      const sx = k % 2 ? 1 : -1;
      const z = -FL + (k / 13) * 2 * FL;
      kit.add(dome, k % 3 ? SNOW : SNOW_SHADE, place(sx * (FW + 0.25), g - 0.05, z, k, new THREE.Vector3(0.55 + (k % 3) * 0.1, 0.45 + (k % 4) * 0.08, 0.7)));
    }
    for (let k = 0; k < 5; k++) kit.add(dome, SNOW, place(-FW + k * 0.72, g - 0.05, -FL - 0.3, k, new THREE.Vector3(0.7, 0.65, 0.6)));
    // The stable north of the fort: a low barn of the same stone, its own snowed roof, and its walk-through open on
    // posts between the two yards (the pieces' pile lies there).
    const sz0 = -4.15 * ROW;
    for (const sx of [-1, 1]) kit.add(box, wc(STONE[1] as string, sx * 1.4, sz0), place(sx * 1.4, g + 0.55, sz0, 0, new THREE.Vector3(0.32, 1.1, 1.0)));
    kit.add(box, wc(STONE[2] as string, 0, sz0 - 0.5), place(0, g + 0.55, sz0 - 0.5, 0, new THREE.Vector3(3.1, 1.1, 0.3)));
    kit.add(box, ROOF_WOOD, place(0, g + 1.18, sz0 - 0.05, 0, new THREE.Vector3(3.3, 0.12, 1.35)));
    kit.add(box, SNOW, place(0, g + 1.3, sz0 - 0.05, 0, new THREE.Vector3(3.3, 0.16, 1.3)));
    // The walk-through's lean-to roof on its posts.
    for (const sx of [-1, 1]) for (const dz of [0, 1]) kit.add(box, WOOD[0] as string, place(sx * 1.05, g + 0.5, -3.55 * ROW + dz * 0.95, 0, new THREE.Vector3(0.1, 1.0, 0.1)));
    kit.add(box, ROOF_WOOD, place(0, g + 1.05, -3.1 * ROW, 0, new THREE.Vector3(2.4, 0.08, 1.2), 0.18));
    kit.add(box, SNOW_SHADE, place(0, g + 1.13, -3.1 * ROW, 0, new THREE.Vector3(2.4, 0.07, 1.15), 0.18));
    // The yards' walls: low drystone, snow along their tops; the far yard's mouth open to the slope, two gateposts.
    const wallRun = (x0: number, z0: number, x1: number, z1: number, seed: number): void => {
      const len = Math.hypot(x1 - x0, z1 - z0);
      const n = Math.max(2, Math.round(len / 0.55));
      const yaw = Math.atan2(-(z1 - z0), x1 - x0);
      for (let k = 0; k < n; k++) {
        const t = (k + 0.5) / n;
        const x = x0 + (x1 - x0) * t;
        const z = z0 + (z1 - z0) * t;
        const gy = this.ctx.ground(x, z);
        const hh = 0.62 + ((k * 7 + seed) % 5) * 0.035;
        kit.add(box, wc(STONE[(k + seed) % 4] as string, x, z, 0.8), place(x, gy + hh / 2, z, yaw, new THREE.Vector3(len / n + 0.03, hh, 0.36)));
        kit.add(box, k % 3 ? SNOW : SNOW_SHADE, place(x, gy + hh + 0.05, z, yaw, new THREE.Vector3(len / n + 0.05, 0.1, 0.4)));
      }
    };
    const XO = 5.85;
    const ZS = 4.72 * ROW;
    const ZN = -4.72 * ROW;
    // South wall from end to end (the passage runs inside it), the outer walls, the hearth yard's north wall.
    wallRun(-XO, ZS, XO, ZS, 1);
    wallRun(-XO, ZN, -XO, ZS, 2);
    wallRun(XO, ZN, XO, ZS, 3);
    wallRun(h * XO, ZN, h * 1.6, ZN, 4);
    // The far yard's mouth: the wall stops short, two gateposts, the slope coming down into it.
    wallRun(-h * XO, ZN, -h * 5.0, ZN, 5);
    for (const x of [-h * 2.9, -h * 5.0]) {
      kit.add(box, wc(STONE[0] as string, x, ZN), place(x, g + 0.55, ZN, 0, new THREE.Vector3(0.42, 1.1, 0.42)));
      kit.add(dome, SNOW, place(x, g + 1.12, ZN, 0, new THREE.Vector3(0.26, 0.12, 0.26)));
    }
    // Drifts in the yards' corners, combed by the wind against the walls.
    for (const [x, z] of [[h * 5.2, ZS - 0.4], [-h * 5.2, ZS - 0.4], [h * 5.3, ZN + 0.5], [h * 2.2, ZS - 0.3], [-h * 2.2, ZS - 0.3]] as [number, number][]) {
      kit.add(dome, SNOW_SHADE, place(x, this.ctx.ground(x, z) - 0.05, z, x, new THREE.Vector3(0.9, 0.3, 0.6)));
    }
    // The hearth in its yard: a ring of blackened stones, logs, the ash; its flame and smoke drawn apart (they change).
    for (let k = 0; k < 9; k++) {
      const a = (k / 9) * Math.PI * 2;
      kit.add(rock, wc(DARK_STONE[k % 3] as string, hearthAt.x, hearthAt.z, 0.6), place(hearthAt.x + Math.cos(a) * 0.42, hearthAt.y + 0.08, hearthAt.z + Math.sin(a) * 0.42, a, new THREE.Vector3(0.28, 0.2, 0.24)));
    }
    for (let k = 0; k < 3; k++) kit.add(cyl, '#2a201a', place(hearthAt.x, hearthAt.y + 0.12, hearthAt.z, (k / 3) * Math.PI, new THREE.Vector3(0.11, 0.7, 0.11), Math.PI / 2 - 0.18));
    kit.add(cyl, '#1c1816', place(hearthAt.x, hearthAt.y + 0.02, hearthAt.z, 0, new THREE.Vector3(0.75, 0.04, 0.75)));
    // A stack of firewood against the fort by the hearth, a bench.
    for (let k = 0; k < 6; k++) kit.add(cyl, WOOD[k % 4] as string, place(h * (FW + 0.4), g + 0.1 + (k % 3) * 0.17, FL - 0.4 - Math.floor(k / 3) * 0.2, 0, new THREE.Vector3(0.16, 0.8, 0.16), Math.PI / 2));
    this.buildHearth(hearthAt);
    this.anchors.hearth = hearthAt.clone().setY(hearthAt.y + 1.1);
    this.anchors.door = doorAt.clone();
    // The walk-through's pile of pieces (drawn as the state says) and its anchor.
    const pileAt = this.hex(L.pile.q, L.pile.r);
    this.anchors.pile = pileAt.clone().setY(pileAt.y + 0.9);
    // Past time is cleaner: no frost on the stone (the snow on it is the same).
    void past;
    this.buildRoof(L.hooks, h, kit);
    // The fort's lights: warm from the slits and the door's cracks (the fire inside), on the snow round them.
    this.light(new THREE.Vector3(h * (FW + 0.3), g + 0.4, doorAt.z), FIRE_DEEP, 1.8, 0.28);
    box.dispose();
    dome.dispose();
    cyl.dispose();
    rock.dispose();
  }

  /** The roof on its axis: two slopes of dark boards, their snow, the eaves' hooks; the flag on the ridge. */
  private buildRoof(hooks: [{ q: number; r: number }[], { q: number; r: number }[]], h: number, kit: Kit): void {
    void kit;
    const g = this.ctx.ground(0, 0);
    const roof = new THREE.Group();
    roof.name = 'fort-roof';
    roof.position.set(0, g + RIDGE, 0);
    this.group.add(roof);
    this.roof = roof;
    const len = Math.hypot(EAVE_X, RIDGE - EAVE_Y);
    const ang = Math.atan2(RIDGE - EAVE_Y, EAVE_X);
    const boards = this.std(ROOF_WOOD, { roughness: 0.95 });
    const snowM = this.std(SNOW, { roughness: 0.92, flatShading: false });
    const slopeGeo = this.track(new THREE.BoxGeometry(len, 0.1, 2 * FL + 0.6));
    const slabGeo = this.track(new THREE.BoxGeometry(1, 1, 1));
    // Boards: ridges across the slope (a few slats on top read as boards from above).
    const slatGeo = this.track(new THREE.BoxGeometry(0.06, 0.04, 2 * FL + 0.6));
    for (const sx of [-1, 1]) {
      const slope = new THREE.Group();
      slope.rotation.z = -sx * ang;
      roof.add(slope);
      // The boards and their slats as one mesh (each loose piece on the roof was a draw call: 132 in the test).
      const parts = [slopeGeo.clone().translate(sx * (len / 2), 0, 0)];
      for (let k = 1; k < 5; k++) parts.push(slatGeo.clone().translate(sx * (len * k) / 5, 0.06, 0));
      const merged = this.track(mergeGeometries(parts) as THREE.BufferGeometry);
      for (const g of parts) g.dispose();
      const m = new THREE.Mesh(merged, boards);
      m.castShadow = true;
      m.receiveShadow = true;
      slope.add(m);
      // The snow on it: a slab over the boards, thicker with each count of the sim; bowed in the middle.
      const slab = new THREE.Mesh(slabGeo, snowM);
      slab.position.set(sx * (len / 2 - 0.08), 0.1, 0);
      slab.scale.set(len - 0.3, 0.05, 2 * FL + 0.2);
      slab.castShadow = true;
      slope.add(slab);
      this.slabs[sx === h ? 0 : 1] = slab;
      // The snow's lip curling over the eave (a long half-round), and a few lumps on the slope.
      const lip = new THREE.Mesh(this.track(new THREE.CylinderGeometry(1, 1, 1, 10)), snowM);
      lip.rotation.set(Math.PI / 2, 0, 0);
      lip.position.set(sx * (len - 0.22), 0.1, 0);
      lip.scale.set(0.12, 2 * FL + 0.2, 0.12);
      slope.add(lip);
      this.lips[sx === h ? 0 : 1] = lip;
    }
    // A cap along the ridge, its snow, icicles off both eaves.
    const ridge = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.22, 0.16, 2 * FL + 0.7)), boards);
    ridge.position.y = 0.05;
    roof.add(ridge);
    const ice = this.std(ICE, { roughness: 0.3, metalness: 0.1, transparent: true, opacity: 0.85 });
    const icGeo = this.track(new THREE.ConeGeometry(0.035, 0.22, 5));
    const ics: THREE.BufferGeometry[] = [];
    const mtx = new THREE.Matrix4();
    for (const sx of [-1, 1]) {
      for (let k = 0; k < 12; k++) {
        mtx.compose(new THREE.Vector3(sx * EAVE_X, EAVE_Y - RIDGE - 0.16 - (k % 3) * 0.04, -FL - 0.2 + (k / 11) * (2 * FL + 0.4)), new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI, 0, 0)), new THREE.Vector3(1, 0.7 + ((k * 5) % 4) * 0.25, 1));
        ics.push(icGeo.clone().applyMatrix4(mtx));
      }
    }
    const icMesh = new THREE.Mesh(this.track(mergeGeometries(ics) as THREE.BufferGeometry), ice);
    for (const g of ics) g.dispose();
    roof.add(icMesh);
    // The hooks: iron, hanging under each eave; the pieces hang below them (as the state says). Their marks below.
    const iron = this.std('#2f3238', { metalness: 0.6, roughness: 0.5 });
    const hookGeo = this.track(new THREE.TorusGeometry(0.07, 0.016, 4, 10, Math.PI * 1.3));
    const sides = [h, -h];
    const hookParts: THREE.BufferGeometry[] = [];
    sides.forEach((sx, side) => {
      for (let k = 0; k < 4; k++) {
        const z = (-1.5 + k) * ROW * 1.05;
        mtx.compose(new THREE.Vector3(sx * (EAVE_X - 0.05), EAVE_Y - RIDGE - 0.12, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 2, Math.PI)), new THREE.Vector3(1, 1, 1));
        hookParts.push(hookGeo.clone().applyMatrix4(mtx));
        const slot = new THREE.Group();
        slot.position.set(sx * (EAVE_X - 0.05), EAVE_Y - RIDGE - 0.22, z);
        roof.add(slot);
        (this.slots[side] as THREE.Group[]).push(slot);
        (this.anchors.hooks[side] as THREE.Vector3[]).push(new THREE.Vector3(sx * (EAVE_X + 0.15), this.ctx.ground(0, 0) + EAVE_Y - 0.6, z));
      }
      this.anchors.eaves[side] = new THREE.Vector3(sx * (EAVE_X * 0.7), this.ctx.ground(0, 0) + RIDGE + 0.6, 0);
    });
    roof.add(new THREE.Mesh(this.track(mergeGeometries(hookParts) as THREE.BufferGeometry), iron));
    for (const g of hookParts) g.dispose();
    void hooks;
    // The flag at the ridge's north end: a pole, the pennant flying to the lee side.
    const pole = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.03, 0.035, 1.6, 6)), this.std(WOOD[0] as string));
    pole.position.set(0, 0.8, -FL - 0.05);
    roof.add(pole);
    const fg = new THREE.Group();
    fg.position.set(0, 1.45, -FL - 0.05);
    roof.add(fg);
    const fm = flagMat(COMPANY_RED);
    this.mats.push(fm);
    this.timed.push(fm);
    const pen = new THREE.PlaneGeometry(0.95, 0.34, 14, 1);
    pen.translate(0.475, 0, 0);
    this.track(pen);
    const pm = new THREE.Mesh(pen, fm);
    fg.add(pm);
    this.flag = { g: fg, mat: fm, yaw: 0, want: 0, warnAt: -1 };
    this.anchors.flag = new THREE.Vector3(0, this.ctx.ground(0, 0) + RIDGE + 1.9, -FL);
    // The tip: the curtain off an eave, the cloud over the yard, the mound left there.
    const cm = curtainMat();
    this.mats.push(cm);
    this.timed.push(cm);
    const curtain = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1)), cm);
    curtain.visible = false;
    curtain.renderOrder = 5;
    this.group.add(curtain);
    this.curtain = { mesh: curtain, mat: cm };
    const clm = cloudMat();
    this.mats.push(clm);
    this.timed.push(clm);
    const cloud = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1)), clm);
    cloud.rotation.x = -Math.PI / 2;
    cloud.visible = false;
    cloud.renderOrder = 6;
    this.group.add(cloud);
    this.cloud = { mesh: cloud, mat: clm };
    const mound = new THREE.Mesh(this.track(new THREE.SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2)), snowM);
    mound.visible = false;
    mound.receiveShadow = true;
    this.group.add(mound);
    this.mound = mound;
  }

  /** The hearth's flame (lit) and smoke (cold): apart from the dressing, they change with the fight. */
  private buildHearth(at: THREE.Vector3): void {
    const fm = flameMat();
    this.mats.push(fm);
    this.timed.push(fm);
    const geo = new FlameKit();
    geo.add(at.clone().setY(at.y + 0.1), 0.95, 0.3);
    geo.add(at.clone().add(new THREE.Vector3(0.18, 0.08, 0.1)), 0.6, 1.7);
    geo.add(at.clone().add(new THREE.Vector3(-0.15, 0.08, -0.12)), 0.55, 2.9);
    const flame = geo.build(this, fm) as THREE.Mesh;
    this.group.add(flame);
    const sm = smokeMat();
    this.mats.push(sm);
    this.timed.push(sm);
    const smoke = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.9, 2.6)), sm);
    smoke.position.set(at.x, at.y + 1.35, at.z);
    smoke.renderOrder = 4;
    this.group.add(smoke);
    this.hearth = { flame, smoke, smokeMat: sm, at: at.clone(), lit: true };
    this.light(at.clone().setY(at.y + 0.5), FIRE, 2.6, 0.75);
    this.light(at.clone().setY(at.y + 0.04), FIRE_DEEP, 5.5, 0.32);
  }

  /** The high farms of Musgoviejo across the valley: a scatter of warm lights far down to the south. */
  private buildFarms(): void {
    // Clusters (a farm each: a window or two, a lantern by the byre) on the slope across the valley.
    for (let f = 0; f < 9; f++) {
      const cx = -34 + f * 8.5 + Math.sin(f * 4.1) * 3;
      const cz = 58 + Math.sin(f * 2.3) * 5;
      const cy = -2.5 + Math.sin(f * 1.7) * 1.2 + (cz - 58) * 0.3;
      const n = 2 + (f % 3);
      for (let i = 0; i < n; i++) this.light(new THREE.Vector3(cx + Math.sin(i * 5.3 + f) * 1.4, cy + (i % 2) * 0.5, cz + Math.cos(i * 3.7) * 1.0), FARM, 1.7 + (i % 2) * 0.8, 0.95);
      this.light(new THREE.Vector3(cx, cy - 0.5, cz), FARM, 6, 0.12);
    }
  }

  /* ---------- the climb ---------- */

  /** The trail between ice and fallen rock, the fort's front at the top, Kel's torch, the rocks' shadows. */
  private buildSlope(kit: Kit, flames: FlameKit, s: BattleState): void {
    const L = slopeLayout(s.seed);
    const M = L.radius + 2;
    const dodec = new THREE.DodecahedronGeometry(0.5, 0);
    const cone = new THREE.ConeGeometry(0.5, 1, 6);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dome = new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    // Each rock cell beside the trail: a boulder or a block of grey ice, bigger the farther out (a slope of scree
    // and ice rising either side); snow caught on top.
    for (const hx of L.rocks) {
      const x2 = 2 * hx.q + hx.r;
      const mid = L.mid[hx.r + M] ?? 0;
      const off = Math.abs(x2 - mid) - 5;
      if (off < 1 || off > 9 || hx.r < -(L.radius - 1) - 1) continue;
      // Off the cell's middle, of all sizes, some cells left bare: scree, not a floor of hexagons.
      const k = Math.sin(hx.q * 12.9 + hx.r * 78.2) * 0.5 + 0.5;
      const k2 = Math.sin(hx.q * 39.3 + hx.r * 11.7) * 0.5 + 0.5;
      if (off > 2 && k2 < 0.3) continue;
      const p = this.hex(hx.q, hx.r).add(new THREE.Vector3((k2 - 0.5) * 0.9, 0, (k - 0.5) * 0.8));
      const big = (0.55 + Math.min(1.8, off * 0.28)) * (0.6 + k * 0.8);
      if ((hx.q + hx.r * 3) % 4 === 0) {
        // Ice: a tilted shard, pale blue, with a cap.
        kit.add(cone, ICE, place(p.x, p.y + big * 0.35, p.z, k * 6, new THREE.Vector3(big * 0.55, big * 1.2, big * 0.45), 0.2 * (k - 0.5), 0.3 * (k - 0.5)));
      } else {
        kit.add(dodec, ROCK[Math.floor(k * 3.99)] as string, place(p.x, p.y + big * 0.25, p.z, k * 4 + k2 * 3, new THREE.Vector3(big * (1.0 + k2 * 0.5), big * (0.6 + k * 0.5), big * (0.9 + k2 * 0.3)), (k - 0.5) * 0.5, (k2 - 0.5) * 0.4));
        if (k2 > 0.35) kit.add(dodec, SNOW, place(p.x + 0.05, p.y + big * (0.5 + k * 0.2), p.z, k * 4 + 1, new THREE.Vector3(big * 0.75, big * 0.2, big * 0.7)));
        // A smaller stone at its foot now and then.
        if (k > 0.6) kit.add(dodec, ROCK[(Math.floor(k2 * 3.99) + 1) % 4] as string, place(p.x + (k - 0.5) * 1.1, p.y + 0.1, p.z - (k2 - 0.5) * 1.0, k2 * 5, new THREE.Vector3(0.35, 0.25, 0.3)));
      }
    }
    // The fort's front above the door: its south gable and door, the roof bowed under the snow, buried in drifts.
    const top = L.door;
    const dz = top.r * ROW - 2.0;
    const dx = top.x2 * 0.5 * S;
    const g = this.ctx.ground(dx, dz + 1.2);
    for (const sx of [-1, 1]) kit.add(box, STONE[1] as string, place(dx + sx * FW, g + WALL / 2, dz - 1.2, 0, new THREE.Vector3(0.36, WALL, 3.2)));
    kit.add(box, STONE[2] as string, place(dx, g + WALL / 2, dz + 0.4, 0, new THREE.Vector3(2 * FW + 0.36, WALL, 0.36)));
    for (let k = 0; k < 6; k++) {
      const y = WALL + (k + 0.5) * ((RIDGE - WALL) / 6);
      kit.add(box, STONE[k % 4] as string, place(dx, g + y, dz + 0.4, 0, new THREE.Vector3(2 * FW * (1 - (k + 0.5) / 6.3), (RIDGE - WALL) / 6 + 0.01, 0.32)));
    }
    const ang = Math.atan2(RIDGE - EAVE_Y, EAVE_X);
    const len = Math.hypot(EAVE_X, RIDGE - EAVE_Y);
    for (const sx of [-1, 1]) {
      kit.add(box, ROOF_WOOD, place(dx + sx * EAVE_X * 0.5, g + (RIDGE + EAVE_Y) / 2 - 0.05, dz - 1.1, 0, new THREE.Vector3(len, 0.1, 3.4), 0, -sx * ang));
      kit.add(box, SNOW, place(dx + sx * EAVE_X * 0.5, g + (RIDGE + EAVE_Y) / 2 + 0.1, dz - 1.1, 0, new THREE.Vector3(len, 0.24, 3.3), 0, -sx * ang - sx * 0.03));
    }
    kit.add(box, '#2a241f', place(dx, g + 0.6, dz + 0.6, 0, new THREE.Vector3(0.75, 1.15, 0.06)));
    for (let k = 0; k < 7; k++) kit.add(dome, k % 2 ? SNOW : SNOW_SHADE, place(dx - 2.4 + k * 0.8, g - 0.05, dz + 0.7, k, new THREE.Vector3(0.6, 0.45 + (k % 3) * 0.12, 0.5)));
    const doorAt = new THREE.Vector3(dx, g, dz + 0.7);
    this.anchors.door = doorAt.clone().setY(g + 1.6);
    // Torches either side of the door.
    for (const sx of [-1, 1]) {
      const p = new THREE.Vector3(dx + sx * 0.75, g + 1.25, dz + 0.65);
      kit.add(box, WOOD[2] as string, place(p.x, p.y - 0.2, p.z, 0, new THREE.Vector3(0.06, 0.4, 0.06)));
      flames.add(p, 0.32, sx + 3);
      this.light(p.clone().setY(p.y + 0.15), FIRE, 1.6, 0.55);
      this.light(new THREE.Vector3(p.x, g + 0.05, p.z + 0.4), FIRE_DEEP, 3.0, 0.2);
    }
    // Kel's torch at the landing: a pole in the snow, its flame (shown once planted).
    const tc = hexToWorld(L.torch.q, L.torch.r);
    const tp = this.ctx.at(tc.x, tc.y);
    const tg = new THREE.Group();
    tg.position.copy(tp);
    const pole = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.035, 0.045, 1.1, 6)), this.std(WOOD[0] as string));
    pole.position.y = 0.5;
    tg.add(pole);
    const tf = new FlameKit();
    tf.add(new THREE.Vector3(0, 1.08, 0), 0.5, 4.2);
    const fm = flameMat();
    this.mats.push(fm);
    this.timed.push(fm);
    const fl = tf.build(this, fm);
    if (fl) tg.add(fl);
    const ring = new THREE.Mesh(this.track(new THREE.RingGeometry(0.55, 0.78, 28)), new THREE.MeshBasicMaterial({ color: '#ff8a3a', transparent: true, opacity: 0.35, depthWrite: false }));
    this.mats.push(ring.material as THREE.Material);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.04;
    tg.add(ring);
    tg.visible = false;
    this.group.add(tg);
    this.torch = { g: tg, at: tp };
    this.anchors.torch = tp.clone().setY(tp.y + 1.6);
    // The rocks' shadows (a pool, reused) and the rocks that land in them.
    const shGeo = this.track(new THREE.CircleGeometry(1, 24));
    const rockGeo = this.track(new THREE.DodecahedronGeometry(0.42, 0));
    const rockMat = this.std(ROCK[1] as string);
    for (let i = 0; i < 6; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: '#1a2230', transparent: true, opacity: 0, depthWrite: false });
      this.mats.push(mat);
      const mesh = new THREE.Mesh(shGeo, mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.renderOrder = 3;
      mesh.visible = false;
      this.group.add(mesh);
      const rock = new THREE.Mesh(rockGeo, rockMat);
      rock.visible = false;
      rock.castShadow = true;
      this.group.add(rock);
      this.shadows.push({ mesh, mat, rock });
    }
    dodec.dispose();
    cone.dispose();
    box.dispose();
    dome.dispose();
  }

  /* ---------- the hall ---------- */

  /** The hall inside: its walls, the great hearth, the long table with eleven places, the dead or the living at it. */
  private buildHall(kit: Kit, flames: FlameKit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const dome = new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const g = this.ctx.ground(0, 0);
    const W = 4.1;
    const D = 2.65;
    const lit = this.place !== 'fortTomb';
    // The great hearth in the north wall: a stone mouth, its hood up the wall, the fire in it (cold in the tomb).
    const hz = -D + 0.35;
    const fireAt = new THREE.Vector3(0, g + 0.1, hz + 0.1);
    const fires = lit ? [fireAt] : [];
    const torchAt: THREE.Vector3[] = [];
    for (const [x, z] of [[-W + 0.15, -0.6], [W - 0.15, 0.6], [-1.8, D - 0.15]] as [number, number][]) torchAt.push(new THREE.Vector3(x, g + 1.4, z));
    const all = [...fires, ...torchAt];
    // The tomb is lit by one torch only, cold; the hearth's scene by the great fire, kept from flooding the walls orange.
    const wc = (c: string, x: number, z: number): THREE.Color => this.warm(c, x, z, lit ? all : torchAt.slice(2), lit ? (this.place === 'fortHearth' ? 0.3 : 0.55) : 0.4);
    // The walls, a course of stone at a time; the rafters across, open to the night (the camera looks in).
    const wall = (x0: number, z0: number, x1: number, z1: number, seed: number): void => {
      const len = Math.hypot(x1 - x0, z1 - z0);
      const n = Math.max(2, Math.round(len / 0.7));
      const yaw = Math.atan2(-(z1 - z0), x1 - x0);
      for (let k = 0; k < n; k++) {
        const t = (k + 0.5) / n;
        const x = x0 + (x1 - x0) * t;
        const z = z0 + (z1 - z0) * t;
        // Courses of uneven stones: each a little off the course's colour and depth (a checkerboard of four greys
        // read as tiles).
        for (let c = 0; c < 4; c++) {
          const j = Math.sin((k + 1) * 12.9 + c * 7.1 + seed) * 0.5 + 0.5;
          const col = new THREE.Color(STONE[1] as string).lerp(new THREE.Color(STONE[(k * 3 + c) % 4] as string), 0.45).multiplyScalar(0.62 + j * 0.18);
          kit.add(box, wc('#' + col.getHexString(), x, z), place(x + (c % 2 ? 0.08 : -0.05) * Math.cos(yaw), g + 0.23 + c * 0.46, z, yaw, new THREE.Vector3(len / n - 0.02 + j * 0.05, 0.43, 0.38 + j * 0.06)));
        }
      }
    };
    wall(-W, -D, W, -D, 0);
    wall(-W, D, W, D, 1);
    wall(-W, -D, -W, D, 2);
    wall(W, -D, W, D, 3);
    for (let k = 0; k < 5; k++) kit.add(box, WOOD[k % 4] as string, place(-W + 0.8 + k * 1.6, g + 1.95, 0, 0, new THREE.Vector3(0.18, 0.18, 2 * D + 0.4)));
    kit.add(box, WOOD[0] as string, place(0, g + 2.0, 0, 0, new THREE.Vector3(2 * W + 0.4, 0.2, 0.2)));
    // The hearth's mouth and hood.
    kit.add(box, wc(DARK_STONE[0] as string, 0, hz), place(0, g + 0.55, hz - 0.15, 0, new THREE.Vector3(1.9, 1.1, 0.5)));
    kit.add(box, '#120f0d', place(0, g + 0.42, hz + 0.02, 0, new THREE.Vector3(1.1, 0.84, 0.3)));
    kit.add(box, wc(STONE[2] as string, 0, hz), place(0, g + 1.3, hz - 0.05, 0, new THREE.Vector3(2.1, 0.4, 0.7)));
    kit.add(box, wc(STONE[1] as string, 0, hz), place(0, g + 1.75, hz - 0.15, 0, new THREE.Vector3(1.3, 0.5, 0.5)));
    for (let k = 0; k < 3; k++) kit.add(cyl, '#2a201a', place((k - 1) * 0.22, g + 0.12, hz + 0.1, 0, new THREE.Vector3(0.12, 0.8, 0.12), Math.PI / 2, 0.2));
    if (lit) {
      flames.add(fireAt.clone().setY(g + 0.12), 0.85, 0.7);
      flames.add(fireAt.clone().add(new THREE.Vector3(0.25, 0.05, 0)), 0.55, 1.9);
      flames.add(fireAt.clone().add(new THREE.Vector3(-0.22, 0.05, 0.05)), 0.6, 2.6);
      // (Kept small: a big additive glow near the camera washed the whole frame orange.)
      this.light(fireAt.clone().setY(g + 0.45), FIRE, 1.5, 0.7);
      this.light(fireAt.clone().setY(g + 0.04).setZ(fireAt.z + 0.5), FIRE_DEEP, 2.2, 0.2);
    } else {
      // Cold: ash and frost on the stone, a drift blown in at the hearth's mouth.
      kit.add(dome, SNOW_SHADE, place(0, g, hz + 0.3, 0, new THREE.Vector3(0.7, 0.18, 0.4)));
    }
    // Torches in iron sconces on the walls (lit at the table and the hearth; one guttering in the tomb).
    torchAt.forEach((p, i) => {
      kit.add(box, '#2b2622', place(p.x, p.y - 0.12, p.z, 0, new THREE.Vector3(0.1, 0.12, 0.1)));
      if (lit || i === 2) {
        flames.add(p, 0.34, 5 + i);
        this.light(p.clone().setY(p.y + 0.15), FIRE, 0.9, 0.45);
      }
    });
    // The long table down the middle (not in the last room: there it lies pushed to the wall), its benches, eleven
    // places (a bowl and a cup each): five a side and one at its east end. The benches sit on the hex rows (the
    // figures sit on them: north bench r = −1, south r = 0).
    const tz = -0.52;
    const tx = 0.35;
    if (this.place === 'fortTable') {
      kit.add(box, wc(WOOD[3] as string, tx, tz), place(tx, g + 0.42, tz, 0, new THREE.Vector3(6.5, 0.07, 0.72)));
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) kit.add(box, WOOD[2] as string, place(tx + sx * 3.0, g + 0.2, tz + sz * 0.26, 0, new THREE.Vector3(0.1, 0.4, 0.1)));
      for (const bz of [-ROW, 0]) {
        // Low benches (the sitting pose sits on the floor: a plank under them, not a seat they'd sink into).
        kit.add(box, wc(WOOD[1] as string, tx, bz), place(tx, g + 0.06, bz, 0, new THREE.Vector3(6.3, 0.08, 0.34)));
      }
      const places: { x: number; z: number; empty: boolean }[] = [
        ...[-1.8, -0.6, 0.6, 1.8, 3.0].map((x, i) => ({ x, z: tz - 0.22, empty: [0, 3, 4].includes(i) })),
        ...[-2.4, -1.2, 0, 1.2, 2.4].map((x, i) => ({ x, z: tz + 0.22, empty: i === 4 })),
        { x: 3.45, z: tz, empty: true },
      ];
      for (const pl of places) {
        const empty = this.place === 'fortTable' && pl.empty;
        kit.add(cyl, '#8d8274', place(pl.x, g + 0.47, pl.z, 0, new THREE.Vector3(0.24, 0.025, 0.24)));
        // The empty places sixty years back: the cup turned down, a strip of the Company's red across the bowl.
        kit.add(cyl, empty ? '#3a3330' : (WOOD[3] as string), place(pl.x + 0.16, g + 0.52, pl.z, 0, new THREE.Vector3(0.07, empty ? 0.07 : 0.11, 0.07)));
        if (empty) kit.add(box, COMPANY_RED, place(pl.x, g + 0.49, pl.z, 0.4, new THREE.Vector3(0.2, 0.012, 0.05)));
      }
      // Ruy's diary open at his place.
      kit.add(box, '#4a3a2c', place(-2.4, g + 0.48, tz + 0.05, 0.2, new THREE.Vector3(0.36, 0.03, 0.26)));
      kit.add(box, '#e4ddd0', place(-2.4, g + 0.5, tz + 0.05, 0.2, new THREE.Vector3(0.33, 0.012, 0.24)));
    } else if (this.place === 'fortTomb') {
      // The last room: the table pushed to the south wall, a stool on its side, a bowl on the floor.
      kit.add(box, '#4a4038', place(0.6, g + 0.55, D - 0.55, 0.05, new THREE.Vector3(3.2, 0.08, 0.6)));
      kit.add(box, '#3a3430', place(-1.6, g + 0.2, 0.6, 1.1, new THREE.Vector3(0.36, 0.36, 0.36), 1.57));
      kit.add(cyl, '#5b5f66', place(1.2, g + 0.02, -0.4, 0, new THREE.Vector3(0.24, 0.03, 0.24)));
      // Ruy's diary on the hearth's step, where they left it.
      kit.add(box, '#4a3a2c', place(0.7, g + 0.04, hz + 0.55, 0.4, new THREE.Vector3(0.34, 0.05, 0.24)));
    }
    // Shields of the Company on the walls (the hall's own, not the eleven).
    if (this.place !== 'fortTomb') {
      for (const [x, z, yaw] of [[-W + 0.25, 0.9, Math.PI / 2], [W - 0.25, -0.9, -Math.PI / 2]] as [number, number, number][]) {
        const sg = new THREE.Group();
        sg.position.set(x, g + 1.25, z);
        sg.rotation.y = yaw;
        companyShield(sg, 0.9);
        this.group.add(sg);
      }
    }
    // The last room's dead: Sanne and Tomé sitting against the north wall, frost on them, their shields across their knees.
    if (this.place === 'fortTomb') this.buildDead(kit, g);
    // Straw and snow on the floor along the walls.
    for (let k = 0; k < 10; k++) {
      const a = (k / 10) * Math.PI * 2;
      kit.add(dome, k % 2 ? SNOW_SHADE : '#7a7468', place(Math.cos(a) * (W - 0.5), g - 0.02, Math.sin(a) * (D - 0.45), a, new THREE.Vector3(0.5, 0.08, 0.3)));
    }
    box.dispose();
    cyl.dispose();
    dome.dispose();
  }

  /** Sanne and Tomé, sitting where they stayed, against the wall: frost-grey, rimed, the shields over their knees. */
  private buildDead(kit: Kit, g: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const sph = new THREE.SphereGeometry(0.5, 10, 7);
    const cone = new THREE.ConeGeometry(0.5, 1, 9, 1, true);
    const frost = '#8e98a6';
    const cloak = '#5b6573';
    const rime = '#dfe7f1';
    [-1.55, 1.55].forEach((x, i) => {
      const z = -2.12;
      const sg = new THREE.Group();
      sg.position.set(x, g, z);
      this.group.add(sg);
      // Sitting: legs out, back to the wall, head bowed a little toward the other.
      kit.add(box, frost, place(x - 0.07, g + 0.12, z + 0.32, 0, new THREE.Vector3(0.12, 0.12, 0.42)));
      kit.add(box, frost, place(x + 0.07, g + 0.12, z + 0.32, 0, new THREE.Vector3(0.12, 0.12, 0.42)));
      kit.add(box, frost, place(x, g + 0.48, z + 0.04, 0, new THREE.Vector3(0.32, 0.5, 0.22), -0.12));
      kit.add(cone, cloak, place(x, g + 0.4, z + 0.0, 0, new THREE.Vector3(0.6, 0.78, 0.4)));
      kit.add(sph, frost, place(x + (i === 0 ? 0.04 : -0.04), g + 0.86, z + 0.1, 0, new THREE.Vector3(0.22, 0.24, 0.22)));
      kit.add(sph, rime, place(x, g + 0.94, z + 0.08, 0, new THREE.Vector3(0.2, 0.07, 0.19)));
      kit.add(sph, rime, place(x, g + 0.72, z + 0.06, 0, new THREE.Vector3(0.36, 0.08, 0.2)));
      for (const k of [-1, 1]) kit.add(box, cloak, place(x + k * 0.17, g + 0.4, z + 0.2, 0, new THREE.Vector3(0.08, 0.08, 0.32), -0.6));
      // The shield across the knees, its cross dulled by frost but red.
      const sh = companyShield(sg, 1.0);
      sh.position.set(0, 0.36, 0.42);
      sh.rotation.x = -1.2;
      sh.rotation.z = i === 0 ? 0.15 : -0.1;
    });
    box.dispose();
    sph.dispose();
    cone.dispose();
  }

  /* ---------- as the fight goes ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'tip') {
        const side = e.n ?? 1;
        const sx = side === 0 ? this.side : -this.side;
        this.startTip(sx, side === 3);
      } else if (e.kind === 'rock') this.ctx.shake(0.18);
      else if (e.kind === 'windWarn' && this.flag) this.flag.warnAt = this.now;
    }
  }

  /** The roof tips toward `sx` (−1 west, +1 east): the swing, the curtain, the cloud, the mound. */
  startTip(sx: number, last = false): void {
    this.tip = { at: this.now, sx };
    this.ctx.shake(last ? 0.5 : 0.35);
    const g = this.ctx.ground(0, 0);
    if (this.curtain) {
      const c = this.curtain.mesh;
      c.position.set(sx * (EAVE_X + 0.15), g + EAVE_Y / 2 + 0.2, 0);
      c.rotation.set(0, Math.PI / 2, 0);
      c.scale.set(2 * FL + 0.4, EAVE_Y + 0.6, 1);
      c.visible = true;
    }
    if (this.cloud) {
      this.cloud.mesh.position.set(sx * 3.6, g + 0.35, 0);
      this.cloud.mesh.visible = true;
    }
    if (this.mound) {
      this.mound.position.set(sx * 3.3, g - 0.05, 0);
      this.mound.visible = true;
    }
  }

  /** Every drawn frame: the snow on each slope, the pieces on the hooks, the flag's side, the hearth, the climb's rocks. */
  draw(s: BattleState): void {
    const w = s.senda?.roof;
    const r = s.rules?.senda?.roof;
    if (w && r) {
      w.snow.forEach((n, side) => {
        const slab = this.slabs[side];
        if (!slab) return;
        const th = 0.05 + n * 0.045;
        slab.scale.y = th;
        slab.position.y = 0.06 + th / 2;
        const lip = this.lips[side];
        if (lip) {
          lip.scale.set(0.06 + n * 0.02, lip.scale.y, 0.06 + n * 0.025);
          lip.position.y = 0.06 + th * 0.6;
        }
      });
      const key = w.on.join(',');
      if (key !== this.pieceKey) {
        this.pieceKey = key;
        this.hangPieces(r, w.on);
      }
      const lee = (r.wind[w.wind] as { to: number } | undefined)?.to ?? 0;
      if (this.flag) this.flag.want = (lee === 0 ? this.side : -this.side) > 0 ? 0 : Math.PI;
      if (this.hearth && this.hearth.lit !== w.lit) {
        this.hearth.lit = w.lit;
        this.hearth.flame.visible = w.lit;
      }
    } else if (this.slabs.length && !w) {
      // A scene: the snow the doc's cinematic shows (heavy, the roof bowed).
      for (const slab of this.slabs) {
        slab.scale.y = 0.3;
        slab.position.y = 0.06 + 0.15;
      }
      for (const lip of this.lips) {
        lip.scale.set(0.24, lip.scale.y, 0.26);
        lip.position.y = 0.24;
      }
    }
    const sl = s.senda?.slope;
    if (sl && this.torch) this.torch.g.visible = sl.torch;
    if (sl) this.shadowState = sl.rocks;
    this.tickNow = s.tick;
  }

  /** The pieces on each eave's hooks (shields with their crosses, sacks), and the rest on the pile. */
  private hangPieces(r: NonNullable<NonNullable<BattleState['rules']>['senda']>['roof'] & object, on: number[]): void {
    for (const side of [0, 1]) for (const slot of this.slots[side] as THREE.Group[]) slot.clear();
    for (const p of this.pile) this.group.remove(p);
    this.pile = [];
    const used = [0, 0];
    const sackMat = this.sackMat();
    on.forEach((where, i) => {
      const piece = r.pieces[i] as { w: number; sack: boolean };
      const make = (): THREE.Group => {
        const g = new THREE.Group();
        if (piece.sack) {
          const m = new THREE.Mesh(this.sackGeo(), sackMat);
          m.scale.set(0.22, 0.3, 0.2);
          m.position.y = -0.22;
          g.add(m);
        } else {
          const sh = companyShield(g, 0.7);
          sh.position.y = -0.28;
        }
        return g;
      };
      if (where === 0 || where === 1) {
        const slot = (this.slots[where] as THREE.Group[])[used[where] as number];
        used[where] = (used[where] as number) + 1;
        if (!slot) return;
        const g = make();
        // Face out from the eave.
        const sx = where === 0 ? this.side : -this.side;
        g.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
        slot.add(g);
      } else if (where === -1 && this.anchors.pile) {
        const g = make();
        const k = this.pile.length;
        g.position.set(this.anchors.pile.x + ((k % 4) - 1.5) * 0.28, this.anchors.pile.y - 0.45, this.anchors.pile.z + Math.floor(k / 4) * 0.3 - 0.15);
        g.rotation.set(-0.35, 0.3 * (k % 3), 0);
        this.group.add(g);
        this.pile.push(g);
      }
    });
    void r;
  }

  private sG: THREE.BufferGeometry | null = null;
  private sM: THREE.Material | null = null;
  private sackGeo(): THREE.BufferGeometry {
    return (this.sG ??= this.track(new THREE.SphereGeometry(1, 9, 7)));
  }
  private sackMat(): THREE.Material {
    return (this.sM ??= this.std('#7a6448', { roughness: 1 }));
  }

  frame(now: number): void {
    this.now = now;
    for (const m of this.timed) (m.uniforms.uTime as THREE.IUniform<number>).value = now;
    // The glows, flickering.
    this.glow.begin();
    for (const gl of this.glows) {
      const f = 0.86 + Math.sin(now * 9.1 + gl.seed) * 0.08 + Math.sin(now * 23.7 + gl.seed * 2) * 0.05;
      this.glow.add(gl.p.x, gl.p.y, gl.p.z, gl.c, gl.size * (0.96 + f * 0.04), gl.a * f);
    }
    if (this.hearth && !this.hearth.lit) this.glow.add(this.hearth.at.x, this.hearth.at.y + 0.1, this.hearth.at.z, FIRE_DEEP, 0.8, 0.18);
    if (this.torch?.g.visible) {
      const p = this.torch.at;
      const f = 0.85 + Math.sin(now * 11) * 0.1;
      this.glow.add(p.x, p.y + 1.15, p.z, FIRE, 1.7, 0.6 * f);
      this.glow.add(p.x, p.y + 0.05, p.z, FIRE_DEEP, 3.4, 0.25 * f);
    }
    this.glow.end();
    // The hearth's smoke, only when cold.
    if (this.hearth) {
      const u = this.hearth.smokeMat.uniforms.uOn as THREE.IUniform<number>;
      u.value += ((this.hearth.lit ? 0 : 1) - u.value) * 0.05;
      this.hearth.smoke.visible = u.value > 0.02;
    }
    // The flag turns to the lee side, shaking wild before the wind turns.
    const fl = this.flag;
    if (fl) {
      let d = fl.want - fl.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      fl.yaw += d * 0.04;
      const warn = fl.warnAt >= 0 ? Math.max(0, 1 - (now - fl.warnAt) / 15) : 0;
      fl.g.rotation.y = fl.yaw + Math.sin(now * 7) * 0.5 * warn;
      (fl.mat.uniforms.uWild as THREE.IUniform<number>).value = 0.5 + warn * 1.6;
    }
    // The tip: the roof swings on its axis and back, the curtain pours, the cloud rolls out, the mound sinks.
    const t = this.tip;
    if (t && this.roof) {
      const k = now - t.at;
      const swing = k < 1.8 ? Math.sin((Math.min(k, 1.8) / 1.8) * Math.PI) * (k < 0.4 ? k / 0.4 : 1) : 0;
      this.roof.rotation.z = -t.sx * 0.5 * swing;
      if (this.curtain) {
        const ck = k < 0.25 ? 0 : Math.max(0, 1 - (k - 0.25) / 1.1);
        (this.curtain.mat.uniforms.uK as THREE.IUniform<number>).value = ck;
        this.curtain.mesh.visible = ck > 0.01;
      }
      if (this.cloud) {
        const ck = k < 0.45 ? 0 : Math.max(0, 1 - (k - 0.45) / 2.6);
        const size = 2.5 + Math.min(1, (k - 0.45) / 1.2) * 5.5;
        this.cloud.mesh.scale.set(size, size * 1.6, 1);
        (this.cloud.mat.uniforms.uK as THREE.IUniform<number>).value = ck;
        this.cloud.mesh.visible = ck > 0.01;
      }
      if (this.mound) {
        const rise = Math.min(1, Math.max(0, (k - 0.4) / 0.3));
        const sink = Math.max(0, 1 - Math.max(0, k - 2.5) / 5);
        const hgt = 0.42 * rise * sink;
        this.mound.scale.set(1.9, Math.max(0.001, hgt), 3.4);
        this.mound.visible = hgt > 0.01;
      }
      if (k > 9) this.tip = null;
    }
    // The climb's rocks: the shadow grows darker and smaller as the rock comes; it lands in the last half second.
    if (this.shadows.length) {
      this.shadows.forEach((sh, i) => {
        const x = this.shadowState[i];
        if (!x) {
          sh.mesh.visible = false;
          sh.rock.visible = false;
          return;
        }
        const left = Math.max(0, (x.t - this.tickNow) / 12);
        const p = this.ctx.at(x.x, x.y, 0.03);
        const k = 1 - Math.min(1, left / 3);
        sh.mesh.position.copy(p);
        sh.mesh.scale.setScalar(0.95 - k * 0.25);
        sh.mat.opacity = 0.15 + k * 0.5;
        sh.mesh.visible = true;
        sh.rock.visible = left < 0.6;
        sh.rock.position.set(p.x, p.y + left * 14, p.z);
        sh.rock.rotation.set(now * 3 + i, now * 2, 0);
      });
    }
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
  }
}

/** Flames gathered into one mesh: crossed quads, each with its seed for the flicker. */
class FlameKit {
  private pos: number[] = [];
  private uv: number[] = [];
  private seed: number[] = [];
  private idx: number[] = [];

  add(at: THREE.Vector3, height: number, seed: number): void {
    const w = height * 0.5;
    for (const a of [0, Math.PI / 2]) {
      const dx = Math.cos(a) * w * 0.5;
      const dz = Math.sin(a) * w * 0.5;
      const b = this.pos.length / 3;
      this.pos.push(at.x - dx, at.y, at.z - dz, at.x + dx, at.y, at.z + dz, at.x + dx, at.y + height, at.z + dz, at.x - dx, at.y + height, at.z - dz);
      this.uv.push(0, 0, 1, 0, 1, 1, 0, 1);
      this.seed.push(seed, seed, seed, seed);
      this.idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
  }

  build(owner: FortPlace, mat?: THREE.ShaderMaterial): THREE.Mesh | null {
    if (!this.idx.length) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    geo.setAttribute('aSeed', new THREE.Float32BufferAttribute(this.seed, 1));
    geo.setIndex(this.idx);
    owner.track(geo);
    const m = mat ?? flameMat();
    if (!mat) {
      owner.track(m);
      (owner as unknown as { timed: THREE.ShaderMaterial[] }).timed.push(m);
    }
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false;
    mesh.renderOrder = 7;
    mesh.name = 'fort-flames';
    return mesh;
  }
}

void place;
